import "./locationBillingDetailsEditComponent.scss";
import * as Yup from "yup";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../../../store/reducers";
import {Field, FieldArray, FieldProps, Form, Formik, FormikHelpers, FormikProps, FormikValues} from "formik";
import {CommonService} from "../../../../../shared/services";
import {IAPIResponseType} from "../../../../../shared/models/api.model";
import Grid from "@mui/material/Grid";
import FormikInputComponent from "../../../../../shared/components/core/formik-input/FormikInputComponent";
import FormikSelectDropdownComponent
    from "../../../../../shared/components/core/formik-select-dropdown/FormikSelectDropdownComponent";
import TooltipComponent from "../../../../../shared/components/tooltip/tooltipComponent";
import HelpIcon from "@mui/icons-material/Help";
import FormikCheckboxComponent from "../../../../../shared/components/core/formik-checkbox/FormikCheckboxComponent";
import ButtonComponent from "../../../../../shared/components/core/button/ButtonComponent";
import FromikPhoneNumberComponent
    from "../../../../../shared/components/core/fromik-phone-number/FromikPhoneNumberComponent";
import _ from "lodash";
import LinkComponent from "../../../../../shared/components/link/LinkComponent";

interface LocationBillingDetailsEditComponentProps {
    locationId: number;
    onUpdateComplete: Function;
    onEditCancel: Function;
    locationDetails: any;
}

const PayorMixValidationSchema = Yup.object({
    isSelected: Yup.boolean(),
    estimated: Yup.string().when('isSelected', {
        is: true,
        then: Yup.string().nullable().matches(/^(100|[1-9]?[0-9])$/, 'Estimate must be between 0 - 100').required("Estimated  is required"),
    }),
});

interface payorMixFormSchema {
    title: string;
    estimated: string;
}

const payorMixFormInitialValues: payorMixFormSchema = {
    title: "",
    estimated: ""
}

const LocationBillingDetailsValidationSchema = Yup.object({
    billingUser: Yup.string(),
    firstName: Yup.string().when('billingUser', {
            is: 'other',
            then: Yup.string()
                .required("First Name is required")
                .max(100, "First Name can not be more than 100 characters"),
        }
    ),
    lastName: Yup.string().when('billingUser', {
        is: 'other',
        then: Yup.string()
            .required("Last Name is required")
            .max(100, "Last Name can not be more than 100 characters"),
    }),
    middleName: Yup.string().when('billingUser', {
        is: 'other',
        then: Yup.string()
            .nullable()
            .max(100, "Middle Name can not be more than 100 characters"),
    }),
    emailId: Yup.string().when('billingUser', {
        is: 'other',
        then: Yup.string()
            .nullable()
            .email("Invalid Email ID")
            .matches(/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/, "Enter a valid Email ID")
            .required("Email ID is required"),
    }),
    mobileNumber: Yup.string().when('billingUser', {
        is: 'other',
        then: Yup.string()
            .required("Secondary Mobile Number is required")
            .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits"),
    }),
    faxNumber: Yup.string().when('billingUser', {
        is: 'other',
        then: Yup.string()
            .nullable()
            .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Fax Number should be 10 digits"),
    }),
    secondaryMobileNumber: Yup.string().nullable().when('billingUser', {
        is: 'other',
        then: Yup.string()
            .nullable()
            .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits"),
    }),

    accountExecutiveId: Yup.string().when('billingUser', {
        is: 'other',
        then: Yup.string()
            .required("Account Executive is required"),
    }),
    payorMix: Yup.array(PayorMixValidationSchema),
});

interface LocationBillingDetailsFormSchema {
    billingUser: string;
    firstName: string;
    lastName: string;
    middleName: string;
    mobileNumber: string;
    emailId: string;
    secondaryMobileNumber: string;
    faxNumber: string;
    accountExecutiveId: string;
    payorMix: payorMixFormSchema[]
}

const formInitialValues: LocationBillingDetailsFormSchema = {
    billingUser: "other",
    firstName: "",
    lastName: "",
    middleName: "",
    mobileNumber: "",
    emailId: "",
    secondaryMobileNumber: "",
    faxNumber: "",
    accountExecutiveId: "",
    payorMix: [payorMixFormInitialValues],
}


const LocationBillingDetailsEditComponent = (props: LocationBillingDetailsEditComponentProps) => {
    const {locationDetails, onEditCancel, locationId, onUpdateComplete} = props;
    const [locationBillingDetailsFormInitialValues, setLocationBillingDetailsFormInitialValues] = useState<LocationBillingDetailsFormSchema>(formInitialValues)
    const {accountExecutiveList, payorMixList} = useSelector((state: IRootReducerState) => state.staticData);
    const formikRef = useRef<FormikProps<FormikValues>>(null);

    const onSubmit = useCallback((values: any, {
        setSubmitting,
        setErrors,
    }: FormikHelpers<any>) => {
        const payload = {...values};
        if (payload.billingUser !== 'other') {
            delete payload.firstName;
            delete payload.lastName;
            delete payload.middleName;
            delete payload.emailId;
            delete payload.mobileNumber;
            delete payload.secondaryMobileNumber;
            delete payload.faxNumber;
            delete payload.accountExecutiveId;
        } else {
            payload.mobileNumber = payload.mobileNumber?.replace(/[^0-9]/g, '');
            payload.secondaryMobileNumber = payload?.secondaryMobileNumber?.replace(/[^0-9]/g, '');
            payload.faxNumber = payload?.faxNumber?.replace(/[^0-9]/g, '');
        }
        payload.payorMix = payload.payorMix.filter((payor: any) => payor.isSelected);
        setSubmitting(true);
        CommonService._location.addBillingDetails(locationId, payload)
            .then((response: IAPIResponseType<any>) => {
                setSubmitting(false);
                onUpdateComplete(response.data.billingDetails);
            })
            .catch((error: any) => {
                CommonService.handleErrors(setErrors, error);
                setSubmitting(false);
            });
    }, [locationId, onUpdateComplete]);

    useEffect(() => {
        const payorMixMapping = payorMixList.map((item: any) => {
            return {
                ...item,
                isSelected: false,
                "estimated": ""
            }
        });

        if (formikRef.current) {
            formikRef.current.setFieldValue("payorMix", payorMixMapping);
        }
    }, [payorMixList]);

    const payorMixValuesMaping = useCallback((values: any) => {
        const payorMixMapping = payorMixList?.map((item: any) => {
            return {
                ...item,
                isSelected: false,
                "estimated": ""
            }
        });
        const payorMixMappingList = payorMixMapping?.map((item: any) => {
            values.map((payor: any) => {
                if (item.code === payor.code) {
                    item.isSelected = payor.isSelected;
                    item.estimated = payor.estimated;
                }
                return item;
            })
            return item;
        });
        return payorMixMappingList;
    }, [payorMixList]);

    const handlePopulateAdminDetails = useCallback((type: "adminDetails" | "primaryContactDetails", setFieldValue: (field: string, value: any, shouldValidate?: (boolean | undefined)) => void) => {

        if (!locationDetails?.adminId && type === 'adminDetails') {
            CommonService._alert.showToast("Admin is not assigned to this location", "warning");
            return;
        }
        const {
            firstName = '',
            middleName = '',
            lastName = '',
            mobileNumber = '',
            secondaryMobileNumber = '',
            faxNumber = '',
            emailId = ''
        } = locationDetails?.[type] || {};

        setFieldValue('firstName', firstName)
        setFieldValue('lastName', lastName)
        setFieldValue('middleName', middleName)
        setFieldValue('mobileNumber', CommonService.getMaskedMobileNumber(mobileNumber))
        setFieldValue('secondaryMobileNumber', CommonService.getMaskedMobileNumber(secondaryMobileNumber))
        setFieldValue('faxNumber', CommonService.getMaskedMobileNumber(faxNumber))
        setFieldValue('emailId', emailId)
    }, [locationDetails])

    useEffect(() => {
        // if (locationDetails?.isSameAsAdmin) {
        //     setIsPrimaryContactSameAsAdmin(locationDetails?.isSameAsAdmin);
        // }
        if (locationDetails?.billingDetails?.billingUser) {
            locationDetails.billingDetails.billingUser = 'other'
        }
        if (locationDetails?.billingDetails) {
            const payorMixMapping = payorMixList.map((item: any) => {
                let payorMix = {
                    ...item,
                    isSelected: false,
                    "estimated": ""
                }
                locationDetails?.billingDetails?.payorMix?.forEach((payor: any) => {
                    if (item.title === payor.title) {
                        payorMix = {
                            ...item,
                            isSelected: true,
                            estimated: payor.estimated
                        }
                    }
                })
                return payorMix;
            });
            if (locationDetails?.billingDetails.billingUser === 'other') {
                const locationBillingDetailsInitialValues = _.cloneDeep(locationDetails?.billingDetails);
                locationBillingDetailsInitialValues.mobileNumber = CommonService.getMaskedMobileNumber(locationDetails?.billingDetails.mobileNumber);
                locationBillingDetailsInitialValues.secondaryMobileNumber = CommonService.getMaskedMobileNumber(locationDetails?.billingDetails.secondaryMobileNumber);
                locationBillingDetailsInitialValues.faxNumber = CommonService.getMaskedMobileNumber(locationDetails?.billingDetails?.faxNumber);
                locationBillingDetailsInitialValues.payorMix = payorMixMapping;
                setLocationBillingDetailsFormInitialValues(locationBillingDetailsInitialValues);
            } else {
                setLocationBillingDetailsFormInitialValues({
                    ...formInitialValues,
                    billingUser: locationDetails?.billingDetails.billingUser,
                    payorMix: payorMixMapping,
                });
            }
        }
    }, [payorMixList, locationDetails, payorMixValuesMaping]);

    return (
        <div className={'billing-details-edit-component details-tab-data-content'}>
            <Formik
                validationSchema={LocationBillingDetailsValidationSchema}
                initialValues={locationBillingDetailsFormInitialValues}
                validateOnChange={true}
                validateOnBlur={true}
                validateOnMount={true}
                enableReinitialize={true}
                onSubmit={onSubmit}
                innerRef={formikRef}
            >
                {({isSubmitting, dirty, values, isValid, errors, setFieldValue, validateForm}) => {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                        validateForm();
                    }, [validateForm, values]);
                    return (
                        <Form noValidate={true} className={"facility-admin-details-add-holder"}>
                            <div className="t-form-controls-wrapper">
                                <div className="d-flex align-items-center mrg-bottom-10">
                                    <div className="redirect-new-link-wrapper mrg-bottom-20">
                                        <div>Fetch details of admin?</div>
                                        <LinkComponent
                                            className={"redirect-link"}
                                            onClick={() => handlePopulateAdminDetails("adminDetails", setFieldValue)}>
                                            Fetch Admin Details
                                        </LinkComponent>
                                        <div className={"mrg-left-5 mrg-right-5"}>or</div>
                                        <div>Fetch details of primary contact?</div>
                                        <LinkComponent
                                            className={"redirect-link"}
                                            onClick={() => handlePopulateAdminDetails("primaryContactDetails", setFieldValue)}>
                                            Fetch Primary Contact
                                        </LinkComponent>
                                    </div>

                                    {/*<div className="mrg-bottom-15">Who is tasked with overseeing this role?</div>*/}
                                    {/*<Field*/}
                                    {/*    name={'billingUser'}*/}
                                    {/*>*/}
                                    {/*    {*/}
                                    {/*        (field: FieldProps) => (*/}
                                    {/*            <FormikRadioButtonGroupComponent*/}
                                    {/*                direction={"row"}*/}
                                    {/*                isValueBoolean={false}*/}
                                    {/*                titleKey={'title'}*/}
                                    {/*                valueKey={'code'}*/}
                                    {/*                options={isPrimaryContactSameAsAdmin ? [{*/}
                                    {/*                    title: 'Admin',*/}
                                    {/*                    code: 'admin'*/}
                                    {/*                }, {title: 'Other', code: 'other'}] : [{*/}
                                    {/*                    title: 'Admin',*/}
                                    {/*                    code: 'admin'*/}
                                    {/*                }, {*/}
                                    {/*                    title: 'Primary Contact',*/}
                                    {/*                    code: 'primaryContact'*/}
                                    {/*                }, {title: 'Other', code: 'other'}]}*/}
                                    {/*                formikField={field}*/}
                                    {/*            />*/}
                                    {/*        )*/}
                                    {/*    }*/}
                                    {/*</Field>*/}
                                </div>
                                <>
                                    <div className="pdd-top-15">
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={12} md={6} xl={6}>
                                                <Field name={'firstName'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'First Name'}
                                                                placeholder={'Enter First Name'}
                                                                type={"text"}
                                                                required={true}
                                                                formikField={field}
                                                            />
                                                        )}
                                                </Field>
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={6} xl={6}>
                                                <Field name={'middleName'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Middle Name'}
                                                                placeholder={'Enter Middle Name'}
                                                                type={"text"}
                                                                required={false}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={12} md={6} xl={6}>
                                                <Field name={'lastName'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Last Name'}
                                                                placeholder={'Enter Last Name'}
                                                                type={"text"}
                                                                required={true}
                                                                formikField={field}
                                                            />
                                                        )}
                                                </Field>
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={6} xl={6}>
                                                <Field name={'emailId'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'Email ID'}
                                                                placeholder={'Enter Email ID'}
                                                                type={"email"}
                                                                required={true}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'mobileNumber'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FromikPhoneNumberComponent
                                                                label={'Mobile Number'}
                                                                placeholder={'Mobile Number*'}
                                                                maxLength={14}
                                                                required={true}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'secondaryMobileNumber'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FromikPhoneNumberComponent
                                                                label={'Secondary Mobile Number'}
                                                                placeholder={'Secondary Mobile Number'}
                                                                maxLength={14}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'faxNumber'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FromikPhoneNumberComponent
                                                                label={'Fax Number'}
                                                                placeholder={'(000) 000-0000'}
                                                                maxLength={14}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                        </Grid>
                                    </div>
                                </>
                                <>
                                    <div className="horizontal-line"/>
                                    <div className={'form-sub-title mrg-top-20 mrg-bottom-20'}>Account Executive</div>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'accountExecutiveId'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikSelectDropdownComponent
                                                            label={'Account Executive'}
                                                            required={true}
                                                            multiple={false}
                                                            options={accountExecutiveList}
                                                            valueKey={"id"}
                                                            placeholder={'Select Account Executive'}
                                                            formikField={field}
                                                            getOptionLabel={(option => (option.firstName))}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                </>
                                <div className="horizontal-line"/>
                                <>
                                    <div className="d-flex">
                                        <div className="sub-header">Payor Mix</div>
                                        <div className="mrg-left-15">
                                            <TooltipComponent
                                                placement={'top'}
                                                tooltip={"This panel will be prioritized and executed quickly than others."}
                                                arrow={true}>
                                                <HelpIcon/>
                                            </TooltipComponent>
                                        </div>
                                    </div>
                                    <div className="payor-mix-body">
                                        <FieldArray name="payorMix"
                                                    render={({push: any}) => (
                                                        <>
                                                            {values?.payorMix && values?.payorMix.map((p: any, index: any) => {
                                                                return (
                                                                    <div key={index} className={'mrg-left-10'}>
                                                                        <Grid container>
                                                                            <Grid item sm={12} md={4}>
                                                                                <div
                                                                                    className={p.isSelected ? "mrg-top-10" : ""}>
                                                                                    <Field
                                                                                        name={`payorMix[${index}].isSelected`}>
                                                                                        {
                                                                                            (field: FieldProps) => (
                                                                                                <FormikCheckboxComponent
                                                                                                    label={p?.title}
                                                                                                    required={true}
                                                                                                    id={`p?.title + btn`}
                                                                                                    formikField={field}
                                                                                                />
                                                                                            )
                                                                                        }
                                                                                    </Field>
                                                                                </div>
                                                                            </Grid>
                                                                            <Grid item sm={12} md={4}>
                                                                                {p.isSelected &&
                                                                                    <div
                                                                                        className="payormix-wrapper">
                                                                                        <Field
                                                                                            name={`payorMix[${index}].estimated`}>
                                                                                            {
                                                                                                (field: FieldProps) => (
                                                                                                    <FormikInputComponent
                                                                                                        label={'Estimated'}
                                                                                                        required={true}
                                                                                                        size={"small"}
                                                                                                        id="estimated_input"
                                                                                                        variant={'standard'}
                                                                                                        placeholder={"Estimation"}
                                                                                                        maxLength={3}
                                                                                                        type={"text"}
                                                                                                        formikField={field}
                                                                                                    />
                                                                                                )
                                                                                            }
                                                                                        </Field>
                                                                                        <div
                                                                                            className={'payormix-percentage-icon'}>%
                                                                                        </div>
                                                                                    </div>

                                                                                }
                                                                            </Grid>
                                                                            <Grid item sm={12} md={4}/> </Grid>
                                                                        <Grid item sm={12} md={4}/>

                                                                    </div>
                                                                )
                                                            })
                                                            }
                                                        </>
                                                    )}
                                        />
                                    </div>
                                </>

                            </div>
                            <div className={"form-action-section-edit"}>
                                <ButtonComponent
                                    className={"width-25"}
                                    color={"secondary"}
                                    type={"button"}
                                    variant={"outlined"}
                                    disabled={isSubmitting}
                                    handleClick={() => {
                                        onEditCancel();
                                    }}
                                    fullWidth={false}
                                >
                                    Cancel
                                </ButtonComponent>
                                <ButtonComponent
                                    className={"width-25"}
                                    color={"primary"}
                                    type={"submit"}
                                    variant={"contained"}
                                    disabled={!(isValid && dirty)}
                                    fullWidth={false}
                                    isLoading={isSubmitting}
                                >
                                    Save
                                </ButtonComponent>
                            </div>

                        </Form>
                    )
                }
                }
            </Formik>
        </div>
    );

};

export default LocationBillingDetailsEditComponent;
